/**
 * Chart Modal Component
 *
 * Full-screen modal for displaying dynamic KPI historical charts
 * Slides in from right with backdrop, supports ESC key to close
 */

import { motion, AnimatePresence } from 'framer-motion';
import { useEffect } from 'react';
import { ChartModalBackdrop } from './ChartModalBackdrop';
import { ChartModalHeader } from './ChartModalHeader';
import { ChartModalContent } from './ChartModalContent';
import type { ChartStatistics } from '../../types/chart.types';

interface ChartModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle: string;
  lastUpdated?: Date;
  onExport: () => void;
  data: any[];
  loading: boolean;
  statistics?: ChartStatistics;
  children: React.ReactNode;
}

export function ChartModal({
  isOpen,
  onClose,
  title,
  subtitle,
  lastUpdated,
  onExport,
  data,
  loading,
  statistics,
  children,
}: ChartModalProps) {
  // ESC key to close
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <ChartModalBackdrop onClick={onClose} />

          {/* Modal */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 h-full z-50 overflow-y-auto"
            style={{
              width: '90vw',
              maxWidth: '1400px',
              background:
                'linear-gradient(135deg, rgba(10, 14, 26, 0.98) 0%, rgba(30, 41, 59, 0.95) 100%)',
              backdropFilter: 'blur(12px)',
              borderLeft: '1px solid rgba(0, 212, 255, 0.2)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            }}
            onClick={(e) => e.stopPropagation()} // Prevent close on content click
          >
            <ChartModalHeader
              title={title}
              subtitle={subtitle}
              lastUpdated={lastUpdated}
              onClose={onClose}
              onExport={onExport}
            />

            <ChartModalContent data={data} loading={loading} statistics={statistics}>
              {children}
            </ChartModalContent>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
